import { FC, useMemo } from 'react';
import { Conditional } from '../../../Common/Conditional';
import { LoadingSpinner } from '../../../Common/LoadingSpinner';
import { CmsProduct, ShopContent } from '../../../../types/cms_shared_types';
import { InputParams, ProductInput } from './ProductInput';
import { ProductConfigurationInputs } from './ProductConfigurationModal';
import { UserBooleanInput } from '../../../Common/UserBooleanInput';
import { FormikErrors } from 'formik';
import { SeasonSelection } from './SeasonSelection';

interface ProductConfigurationContentProps {
  values: ProductConfigurationInputs;
  errors: FormikErrors<ProductConfigurationInputs>;
  cmsShopContent: ShopContent;
  loadingPrice: boolean;
  cmsProduct: CmsProduct;
  fieldPrefix?: string;
}

export const ProductConfigurationContent: FC<ProductConfigurationContentProps> = ({
  values,
  errors,
  cmsShopContent,
  loadingPrice,
  cmsProduct,
  fieldPrefix,
}) => {
  const measureInputParams: InputParams = useMemo(
    () => ({
      key: 'measureInput',
      type: cmsProduct.measureInputType,
      min: cmsProduct.measureMin,
      max: cmsProduct.measureMax,
      prompt: cmsProduct.measurePrompt,
      placeholder: cmsProduct.measurePlaceholder,
      options: cmsProduct.measureOptions,
      withCustomLastOption: cmsProduct.measureOptionsWithCustomLastOption,
      customOptionSelectedFieldKey: 'customMeasureInputSelected',
      customOptionNameFieldKey: 'customMeasureInput',
      lastOptionName: cmsProduct.measureOptionsLastOptionName,
      lastOptionPlaceholder: cmsProduct.measureOptionsPlaceholder,
      lastOptionMin: cmsProduct.measureOptionsMin,
      lastOptionMax: cmsProduct.measureOptionsMax,
    }),
    [cmsProduct],
  );

  const timeMeasureInputParams: InputParams = useMemo(
    () => ({
      key: values.isSeasonallyOpen ? '_timeMeasureInputSelection' : 'timeMeasureInput',
      type: cmsProduct.timeMeasureInputType,
      min: cmsProduct.timeMeasureMin,
      max: cmsProduct.timeMeasureMax,
      prompt: cmsProduct.timeMeasurePrompt,
      placeholder: cmsProduct.timeMeasurePlaceholder,
      options: cmsProduct.timeMeasureOptions,
      withCustomLastOption: cmsProduct.timeMeasureOptionsWithCustomLastOption,
      customOptionSelectedFieldKey: `${values.isSeasonallyOpen ? '_' : ''}customTimeMeasureInputSelected`,
      customOptionNameFieldKey: `${values.isSeasonallyOpen ? '_' : ''}customTimeMeasureInput`,
      lastOptionName: cmsProduct.timeMeasureOptionsLastOptionName,
      lastOptionPlaceholder: cmsProduct.timeMeasureOptionsPlaceholder,
      lastOptionMin: cmsProduct.timeMeasureOptionsMin,
      lastOptionMax: cmsProduct.timeMeasureOptionsMax,
    }),
    [cmsProduct, values.isSeasonallyOpen],
  );

  const displayMeasure = cmsProduct.hasMeasure;
  const displayMusicClassToggle = !cmsProduct.isSchablon && cmsProduct.musicClass.includes('B');

  if (loadingPrice) {
    return (
      <div className="flex justify-center mb-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex flex-col sm:grid sm:grid-cols-2 sm:gap-x-10 gap-y-2">
        <Conditional condition={displayMeasure}>
          <ProductInput
            code={cmsProduct.code}
            inputParams={measureInputParams}
            type="Measure"
            fieldPrefix={fieldPrefix}
          />
        </Conditional>
        <Conditional condition={cmsProduct.hasTimeMeasure}>
          <ProductInput
            code={cmsProduct.code}
            inputParams={timeMeasureInputParams}
            type="TimeMeasure"
            fieldPrefix={fieldPrefix}
          />
        </Conditional>
        <Conditional condition={displayMusicClassToggle}>
          <UserBooleanInput
            name={fieldPrefix ? `${fieldPrefix}.musicClassToggleInput` : 'musicClassToggleInput'}
            title={cmsShopContent.productChoiceStep.productConfigurationModal.radioTvTitle}
            description={cmsShopContent.productChoiceStep.productConfigurationModal.radioTvDescription}
          />
        </Conditional>
        <SeasonSelection
          cmsShopContent={cmsShopContent}
          values={values}
          cmsProduct={cmsProduct}
          errors={errors}
          fieldPrefix={fieldPrefix}
        />
      </div>
    </div>
  );
};
